import AppTable from '@/components/AppTable';
import { Table } from 'antd';
import { isArray } from 'lodash';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { formatNumber } from '@/utils/formatters';
import { exportToExcel } from '@/utils';

function DetailTable({
  loading,
  dataSource,
  date,
  uniquePackages,
  packageOption = 1,
}) {
  const detailData = useMemo(() => {
    const dataOfDayObj = {
      reg_total: 0,
      reg_success: 0,
      reg_fail: 0,
      renew_total: 0,
      renew_success: 0,
      renew_fail: 0,
      user_renew_total: 0,
      user_renew_success: 0,
      user_renew_fail: 0,
      cancel_total: 0,
      cancel_user_total: 0,
      cancel_sys_total: 0,
      user_cancel_web: 0,
      user_cancel_ussd: 0,
      user_cancel_sms: 0,
      user_cancel_system: 0,
    }; // Object mẫu để khởi tạo cho mỗi package trong mỗi ngày, tránh bị tham chiếu khi gán cho các package khác nhau trong cùng 1 ngày
    const createPackageStats = () => ({ ...dataOfDayObj }); // Cái này để tránh bị tham chiếu khi gán cho các package khác nhau trong cùng 1 ngày

    if (!dataSource || !isArray(dataSource) || dataSource.length === 0 || !date[0] || !date[1])
      return [];

    const start = dayjs(date[0]); // Ngày bắt đầu
    const end = dayjs(date[1]); // Ngày kết thúc

    const dailyData = new Map(); // Sử dụng Map để lưu dữ liệu theo ngày, mỗi ngày sẽ có một object chứa dữ liệu các package trong ngày đó, giúp dễ dàng truy cập và cập nhật khi lặp qua dataSource

    let currentDate = start; // Bắt đầu từ ngày start, sẽ lặp qua từng ngày cho đến ngày end để đảm bảo có dữ liệu cho tất cả các ngày trong khoảng, kể cả những ngày không có dữ liệu nào trong dataSource
    while (currentDate.isSame(end) || currentDate.isBefore(end)) { // Lăp qua từng ngày từ start đến end
      const dateStr = currentDate.format('DD/MM/YYYY'); // Định dạng ngày theo kiểu 'DD/MM/YYYY' để làm key trong Map, giúp dễ dàng truy cập và cập nhật dữ liệu cho từng ngày
      dailyData.set(dateStr, {
        date: dateStr,
        packages: new Map(),
      }); // Khởi tạo dữ liệu cho ngày hiện tại trong Map, mỗi ngày sẽ có một object chứa trường date và một Map con để lưu dữ liệu theo package, giúp dễ dàng truy cập và cập nhật khi lặp qua dataSource
      currentDate = currentDate.add(1, 'day'); // Tăng ngày hiện tại lên 1 ngày để tiếp tục vòng lặp, đảm bảo sẽ có dữ liệu cho tất cả các ngày trong khoảng từ start đến end, kể cả những ngày không có dữ liệu nào trong dataSource
    }

    dataSource.forEach((item) => {
      const itemDate = dayjs(item.date).format('DD/MM/YYYY');
      const packageKey = item.package_code;

      if (dailyData.has(itemDate)) {
        const dayData = dailyData.get(itemDate);

        if (!dayData.packages.has(packageKey)) {
          dayData.packages.set(packageKey, createPackageStats());
        }

        const packageStats = dayData.packages.get(packageKey);
        packageStats.reg_total += +item.reg_total || 0;
        packageStats.reg_success += +item.reg_success || 0;
        packageStats.reg_fail += +item.reg_fail || 0;
        packageStats.renew_total += +item.renew_total || 0;
        packageStats.renew_success += +item.renew_success || 0;
        packageStats.renew_fail += +item.renew_fail || 0;

        const userRenewTotal = +item.user_renew_total || 0;
        const userRenewSuccess = +item.user_renew_success || 0;
        const userRenewFail = Math.max(0, userRenewTotal - userRenewSuccess); // Tính user_renew_fail dựa trên user_renew_total và user_renew_success, đảm bảo không bị âm

        packageStats.user_renew_total += userRenewTotal;
        packageStats.user_renew_success += userRenewSuccess;
        packageStats.user_renew_fail += userRenewFail;

        // Add to total cancel count
        packageStats.cancel_total += +item.cancel_total || 0;

        // If channel is SYSTEM, add to sys_total
        if (item.channel === 'SYSTEM') {
          packageStats.cancel_sys_total += +item.cancel_total || 0;
        }

        // Parse user_cancel_detail for breakdown display (optional)
        if (item.user_cancel_detail) {
          try {
            const cancelDetail =
              typeof item.user_cancel_detail === 'string'
                ? JSON.parse(item.user_cancel_detail)
                : item.user_cancel_detail;

            packageStats.user_cancel_web = +cancelDetail.web || 0;
            packageStats.user_cancel_ussd = +cancelDetail.ussd || 0;
            packageStats.user_cancel_sms = +cancelDetail.sms || 0;
            packageStats.user_cancel_system = +cancelDetail.system || 0;
          } catch (e) {
            // continue
          }
        }
      }
    });


    // Calculate cancel_user_total = cancel_total - cancel_sys_total for each day and package
    dailyData.forEach((dayData) => {
      dayData.packages.forEach((packageStats) => {
        packageStats.cancel_user_total = packageStats.cancel_total - packageStats.cancel_sys_total;
      });
    });

    const result = Array.from(dailyData.values()).map((dayData) => {
      const row = {
        key: dayData.date,
        date: dayData.date,
      };

      uniquePackages.forEach(({ key }) => {
        const packageStats = dayData.packages.get(key) || createPackageStats();

        if (packageOption === 2) {
          // Retail
          row[`total_${key}`] = packageStats.reg_total + packageStats.renew_total;
          row[`success_${key}`] = packageStats.reg_success + packageStats.renew_success;
          row[`fail_${key}`] = packageStats.reg_fail + packageStats.renew_fail;
        } else {
          // Sub
          row[`register_total_${key}`] = packageStats.reg_total;
          row[`register_success_${key}`] = packageStats.reg_success;
          row[`register_fail_${key}`] = packageStats.reg_fail;

          row[`renew_total_${key}`] = packageStats.renew_total;
          row[`renew_success_${key}`] = packageStats.renew_success;
          row[`renew_fail_${key}`] = packageStats.renew_fail;

          row[`cancel_total_${key}`] = packageStats.cancel_total;
          row[`cancel_user_${key}`] = packageStats.cancel_user_total;
          row[`cancel_system_${key}`] = packageStats.cancel_sys_total;

          // add user renew from dataLogRenew calculated above
          row[`user_renew_total_${key}`] = packageStats.user_renew_total;
          row[`user_renew_success_${key}`] = packageStats.user_renew_success;
          row[`user_renew_fail_${key}`] = packageStats.user_renew_fail;

          row[`user_cancel_web_${key}`] = packageStats.user_cancel_web;
          row[`user_cancel_ussd_${key}`] = packageStats.user_cancel_ussd;
          row[`user_cancel_sms_${key}`] = packageStats.user_cancel_sms;
          row[`user_cancel_system_${key}`] = packageStats.user_cancel_system;
        }
      });

      return row;
    });
    return result ? result.reverse() : [];
  }, [dataSource, date, uniquePackages, packageOption]);

  const calculateTotals = useMemo(() => {
    if (!detailData || detailData.length === 0) return null;

    const totals = { date: 'TOTAL' };

    if (packageOption === 2) {
      // Retail totals
      uniquePackages.forEach((packageName) => {
        totals[`total_${packageName.key}`] = 0;
        totals[`success_${packageName.key}`] = 0;
        totals[`fail_${packageName.key}`] = 0;
      });

      detailData.forEach((row) => {
        uniquePackages.forEach((packageName) => {
          totals[`total_${packageName.key}`] += row[`total_${packageName.key}`] || 0;
          totals[`success_${packageName.key}`] += row[`success_${packageName.key}`] || 0;
          totals[`fail_${packageName.key}`] += row[`fail_${packageName.key}`] || 0;
        });
      });
    } else {
      // Sub package totals
      uniquePackages.forEach((packageName) => {
        totals[`register_total_${packageName.key}`] = 0;
        totals[`register_success_${packageName.key}`] = 0;
        totals[`register_fail_${packageName.key}`] = 0;
        totals[`renew_total_${packageName.key}`] = 0;
        totals[`renew_success_${packageName.key}`] = 0;
        totals[`renew_fail_${packageName.key}`] = 0;
        totals[`user_renew_total_${packageName.key}`] = 0;
        totals[`user_renew_success_${packageName.key}`] = 0;
        totals[`user_renew_fail_${packageName.key}`] = 0;
        totals[`cancel_total_${packageName.key}`] = 0;
        totals[`cancel_user_${packageName.key}`] = 0;
        totals[`cancel_system_${packageName.key}`] = 0;
        totals[`user_cancel_web_${packageName.key}`] = 0;
        totals[`user_cancel_ussd_${packageName.key}`] = 0;
        totals[`user_cancel_sms_${packageName.key}`] = 0;
        totals[`user_cancel_system_${packageName.key}`] = 0;
      });

      detailData.forEach((row) => {
        uniquePackages.forEach((packageName) => {
          totals[`register_total_${packageName.key}`] +=
            row[`register_total_${packageName.key}`] || 0;
          totals[`register_success_${packageName.key}`] +=
            row[`register_success_${packageName.key}`] || 0;
          totals[`register_fail_${packageName.key}`] +=
            row[`register_fail_${packageName.key}`] || 0;
          totals[`renew_total_${packageName.key}`] += row[`renew_total_${packageName.key}`] || 0;
          totals[`renew_success_${packageName.key}`] +=
            row[`renew_success_${packageName.key}`] || 0;
          totals[`renew_fail_${packageName.key}`] += row[`renew_fail_${packageName.key}`] || 0;

          totals[`user_renew_total_${packageName.key}`] +=
            row[`user_renew_total_${packageName.key}`] || 0;
          totals[`user_renew_success_${packageName.key}`] +=
            row[`user_renew_success_${packageName.key}`] || 0;
          totals[`user_renew_fail_${packageName.key}`] +=
            row[`user_renew_fail_${packageName.key}`] || 0;

          totals[`cancel_total_${packageName.key}`] += row[`cancel_total_${packageName.key}`] || 0;
          totals[`cancel_user_${packageName.key}`] += row[`cancel_user_${packageName.key}`] || 0;
          totals[`cancel_system_${packageName.key}`] +=
            row[`cancel_system_${packageName.key}`] || 0;
          totals[`user_cancel_web_${packageName.key}`] +=
            row[`user_cancel_web_${packageName.key}`] || 0;
          totals[`user_cancel_ussd_${packageName.key}`] +=
            row[`user_cancel_ussd_${packageName.key}`] || 0;
          totals[`user_cancel_sms_${packageName.key}`] +=
            row[`user_cancel_sms_${packageName.key}`] || 0;
          totals[`user_cancel_system_${packageName.key}`] +=
            row[`user_cancel_system_${packageName.key}`] || 0;
        });
      });
    }

    return totals;
  }, [detailData, uniquePackages, packageOption]);

  const dataForExport = useMemo(() => {
    if (!detailData) return [];
    if (!calculateTotals) return detailData;
    return [...detailData, calculateTotals];
  }, [detailData, calculateTotals]);

  const detailColsSub = [
    {
      title: 'Charge Time',
      dataIndex: 'date',
      key: 'date',
      fixed: 'left',
      width: 120,
    },
    {
      title: 'Register',
      key: 'register',
      children: [
        {
          title: 'Total',
          key: 'total',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `register_total_${item.key}`,
                key: `register_total_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Success',
          key: 'success',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `register_success_${item.key}`,
                key: `register_success_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Fail',
          key: 'fail',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `register_fail_${item.key}`,
                key: `register_fail_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
      ],
    },
    {
      title: 'Renew',
      key: 'renew',
      children: [
        {
          title: 'Total',
          key: 'total',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `renew_total_${item.key}`,
                key: `renew_total_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Success',
          key: 'success',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `renew_success_${item.key}`,
                key: `renew_success_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Fail',
          key: 'fail',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `renew_fail_${item.key}`,
                key: `renew_fail_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
      ],
    },
    {
      title: 'User Extend',
      key: 'user_extend',
      children: [
        {
          title: 'Total',
          key: 'total',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_renew_total_${item.key}`,
                key: `user_renew_total_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Success',
          key: 'success',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_renew_success_${item.key}`,
                key: `user_renew_success_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'Fail',
          key: 'fail',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_renew_fail_${item.key}`,
                key: `user_renew_fail_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
      ],
    },
    {
      title: 'Cancel',
      key: 'cancel',
      children: [
        {
          title: 'Total',
          key: 'total',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `cancel_total_${item.key}`,
                key: `cancel_total_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'User',
          key: 'user',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `cancel_user_${item.key}`,
                key: `cancel_user_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'System',
          key: 'system',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `cancel_system_${item.key}`,
                key: `cancel_system_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
      ],
    },
    {
      title: 'User Cancel',
      key: 'user_cancel',
      children: [
        {
          title: 'WEB',
          key: 'web',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_cancel_web_${item.key}`,
                key: `user_cancel_web_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'USSD',
          key: 'ussd',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_cancel_ussd_${item.key}`,
                key: `user_cancel_ussd_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'SMS',
          key: 'sms',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_cancel_sms_${item.key}`,
                key: `user_cancel_sms_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
        {
          title: 'System',
          key: 'system',
          children: [
            ...uniquePackages?.map((item) => {
              return {
                title: item.name,
                dataIndex: `user_cancel_system_${item.key}`,
                key: `user_cancel_system_${item.key}`,
                width: 85,
                render: (value) => (value > 0 ? formatNumber(value) : 0),
              };
            }),
          ],
        },
      ],
    },
  ];

  const detailColsRetail = [
    {
      title: 'Charge Time',
      dataIndex: 'date',
      key: 'date',
      fixed: 'left',
      width: 120,
    },
    {
      title: 'Total',
      key: 'total',
      children: [
        ...uniquePackages?.map((item) => ({
          title: item.name,
          dataIndex: `total_${item.key}`,
          key: `total_${item.key}`,
          width: 85,
          render: (value) => (value > 0 ? formatNumber(value) : 0),
        })),
      ],
    },
    {
      title: 'Success',
      key: 'success',
      children: [
        ...uniquePackages?.map((item) => ({
          title: item.name,
          dataIndex: `success_${item.key}`,
          key: `success_${item.key}`,
          width: 85,
          render: (value) => (value > 0 ? formatNumber(value) : 0),
        })),
      ],
    },
    {
      title: 'Fail',
      key: 'fail',
      children: [
        ...uniquePackages?.map((item) => ({
          title: item.name,
          dataIndex: `fail_${item.key}`,
          key: `fail_${item.key}`,
          width: 85,
          render: (value) => (value > 0 ? formatNumber(value) : 0),
        })),
      ],
    },
  ];

  const columnMappingPackage = () => {
    return packageOption === 1
      ? {
        register_total_MAGIC_DAILY: 'Total Register (MAGIC_DAILY)',
        register_total_MAGIC_WEEKLY: 'Total Register (MAGIC_WEEKLY)',
        register_total_MAGIC_MONTHLY: 'Total Register (MAGIC_MONTHLY)',
        register_success_MAGIC_DAILY: 'Success Register (MAGIC_DAILY)',
        register_success_MAGIC_WEEKLY: 'Success Register (MAGIC_WEEKLY)',
        register_success_MAGIC_MONTHLY: 'Success Register (MAGIC_MONTHLY)',
        register_fail_MAGIC_DAILY: 'Fail Register (MAGIC_DAILY)',
        register_fail_MAGIC_WEEKLY: 'Fail Register (MAGIC_WEEKLY)',
        register_fail_MAGIC_MONTHLY: 'Fail Register (MAGIC_MONTHLY)',
        renew_total_MAGIC_DAILY: 'Total Renew (MAGIC_DAILY)',
        renew_total_MAGIC_WEEKLY: 'Total Renew (MAGIC_WEEKLY)',
        renew_total_MAGIC_MONTHLY: 'Total Renew (MAGIC_MONTHLY)',
        renew_success_MAGIC_DAILY: 'Success Renew (MAGIC_DAILY)',
        renew_success_MAGIC_WEEKLY: 'Success Renew (MAGIC_WEEKLY)',
        renew_success_MAGIC_MONTHLY: 'Success Renew (MAGIC_MONTHLY)',
        renew_fail_MAGIC_DAILY: 'Fail Renew (MAGIC_DAILY)',
        renew_fail_MAGIC_WEEKLY: 'Fail Renew (MAGIC_WEEKLY)',
        renew_fail_MAGIC_MONTHLY: 'Fail Renew (MAGIC_MONTHLY)',

        user_renew_total_MAGIC_DAILY: 'User Renew Total (MAGIC_DAILY)',
        user_renew_total_MAGIC_WEEKLY: 'User Renew Total (MAGIC_WEEKLY)',
        user_renew_total_MAGIC_MONTHLY: 'User Renew Total (MAGIC_MONTHLY)',
        user_renew_success_MAGIC_DAILY: 'User Renew Success (MAGIC_DAILY)',
        user_renew_success_MAGIC_WEEKLY: 'User Renew Success (MAGIC_WEEKLY)',
        user_renew_success_MAGIC_MONTHLY: 'User Renew Success (MAGIC_MONTHLY)',
        user_renew_fail_MAGIC_DAILY: 'User Renew Fail (MAGIC_DAILY)',
        user_renew_fail_MAGIC_WEEKLY: 'User Renew Fail (MAGIC_WEEKLY)',
        user_renew_fail_MAGIC_MONTHLY: 'User Renew Fail (MAGIC_MONTHLY)',

        cancel_total_MAGIC_DAILY: 'Total Cancel (MAGIC_DAILY)',
        cancel_total_MAGIC_WEEKLY: 'Total Cancel (MAGIC_WEEKLY)',
        cancel_total_MAGIC_MONTHLY: 'Total Cancel (MAGIC_MONTHLY)',
        cancel_user_MAGIC_DAILY: 'User Cancel (MAGIC_DAILY)',
        cancel_user_MAGIC_WEEKLY: 'User Cancel (MAGIC_WEEKLY)',
        cancel_user_MAGIC_MONTHLY: 'User Cancel (MAGIC_MONTHLY)',
        cancel_system_MAGIC_DAILY: 'System Cancel (MAGIC_DAILY)',
        cancel_system_MAGIC_WEEKLY: 'System Cancel (MAGIC_WEEKLY)',
        cancel_system_MAGIC_MONTHLY: 'System Cancel (MAGIC_MONTHLY)',
        user_cancel_web_MAGIC_DAILY: 'User Cancel WEB (MAGIC_DAILY)',
        user_cancel_web_MAGIC_WEEKLY: 'User Cancel WEB (MAGIC_WEEKLY)',
        user_cancel_web_MAGIC_MONTHLY: 'User Cancel WEB (MAGIC_MONTHLY)',
        user_cancel_ussd_MAGIC_DAILY: 'User Cancel USSD (MAGIC_DAILY)',
        user_cancel_ussd_MAGIC_WEEKLY: 'User Cancel USSD (MAGIC_WEEKLY)',
        user_cancel_ussd_MAGIC_MONTHLY: 'User Cancel USSD (MAGIC_MONTHLY)',
        user_cancel_sms_MAGIC_DAILY: 'User Cancel SMS (MAGIC_DAILY)',
        user_cancel_sms_MAGIC_WEEKLY: 'User Cancel SMS (MAGIC_WEEKLY)',
        user_cancel_sms_MAGIC_MONTHLY: 'User Cancel SMS (MAGIC_MONTHLY)',
        user_cancel_system_MAGIC_DAILY: 'User Cancel System (MAGIC_DAILY)',
        user_cancel_system_MAGIC_WEEKLY: 'User Cancel System (MAGIC_WEEKLY)',
        user_cancel_system_MAGIC_MONTHLY: 'User Cancel System (MAGIC_MONTHLY)',
      }
      : {
        total_MAGIC_BUY3: 'Total (MAGIC_BUY3)',
        total_MAGIC_BUY5: 'Total (MAGIC_BUY5)',
        total_MAGIC_BUY10: 'Total (MAGIC_BUY10)',
        total_MAGIC_BUY50: 'Total (MAGIC_BUY50)',
        success_MAGIC_BUY3: 'Success (MAGIC_BUY3)',
        success_MAGIC_BUY5: 'Success (MAGIC_BUY5)',
        success_MAGIC_BUY10: 'Success (MAGIC_BUY10)',
        success_MAGIC_BUY50: 'Success (MAGIC_BUY50)',
        fail_MAGIC_BUY3: 'Fail (MAGIC_BUY3)',
        fail_MAGIC_BUY5: 'Fail (MAGIC_BUY5)',
        fail_MAGIC_BUY10: 'Fail (MAGIC_BUY10)',
        fail_MAGIC_BUY50: 'Fail (MAGIC_BUY50)',
      };
  };

  const createGroupedHeadersForExport = (packageOption, uniquePackages) => {
    const groups = [];

    if (packageOption === 1) {
      // Sub package structure
      groups.push({
        title: 'Register',
        children: [
          {
            title: 'Total',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Success',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Fail',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
        ],
      });

      groups.push({
        title: 'Renew',
        children: [
          {
            title: 'Total',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Success',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Fail',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
        ],
      });

      groups.push({
        title: 'User Extend',
        children: [
          {
            title: 'Total',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Success',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'Fail',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
        ],
      });

      groups.push({
        title: 'Cancel',
        children: [
          {
            title: 'Total',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'User',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'System',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
        ],
      });

      groups.push({
        title: 'User Cancel Detail',
        children: [
          {
            title: 'WEB',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'USSD',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
          {
            title: 'SMS',
            children: uniquePackages.map((pkg) => ({ title: pkg.name })),
          },
        ],
      });
    } else {
      // Retail package structure
      groups.push({
        title: 'Total',
        children: uniquePackages.map((pkg) => ({ title: pkg.name })),
      });

      groups.push({
        title: 'Success',
        children: uniquePackages.map((pkg) => ({ title: pkg.name })),
      });

      groups.push({
        title: 'Fail',
        children: uniquePackages.map((pkg) => ({ title: pkg.name })),
      });
    }

    return groups;
  };

  const handleExport = async () => {
    if (dataForExport && dataForExport.length > 0) {
      exportToExcel(dataForExport, {
        fileName: `register_detail_${date[0]}_${date[1]}_${packageOption === 1 ? 'sub' : 'retail'}.xlsx`,
        sheetName: 'Register Detail',
        columnMapping: {
          date: 'Charge Time',
          ...columnMappingPackage(),
        },
        styled: true,
        groupedHeaders: createGroupedHeadersForExport(packageOption, uniquePackages),
      });
    } else {
      return;
    }
  };

  return (
    <div className="mt-4 space-y-4 w-full">
      <AppTable
        tableTitle={<p className="font-medium">Detail</p>}
        columns={packageOption === 2 ? detailColsRetail : detailColsSub}
        dataSource={detailData}
        hasPagination={false}
        loading={loading}
        scroll={{
          x: 'max-content',
          y: 600,
        }}
        onExport={handleExport}
        exportTooltip="Export Detail"
        disableColumnVisibility={true}
        summary={(pageData) => {
          if (!pageData || pageData.length === 0) return null;

          const totals = {};

          if (packageOption === 2) {
            // Retail
            uniquePackages.forEach((packageName) => {
              totals[`total_${packageName.key}`] = 0;
              totals[`success_${packageName.key}`] = 0;
              totals[`fail_${packageName.key}`] = 0;
            });

            pageData.forEach((row) => {
              Object.keys(totals).forEach((key) => {
                totals[key] += row[key] || 0;
              });
            });

            const summaryValues = ['Total'];

            ['total', 'success', 'fail'].forEach((metric) => {
              uniquePackages.forEach((packageName) => {
                summaryValues.push(totals[`${metric}_${packageName.key}`] || 0);
              });
            });

            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  {summaryValues.map((value, index) => (
                    <Table.Summary.Cell
                      key={index}
                      index={index}
                      align="center"
                      className="font-medium"
                    >
                      {typeof value === 'number' ? formatNumber(value) : value}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            );
          } else {
            // Sub
            uniquePackages.forEach((packageName) => {
              totals[`register_total_${packageName.key}`] = 0;
              totals[`register_success_${packageName.key}`] = 0;
              totals[`register_fail_${packageName.key}`] = 0;
              totals[`renew_total_${packageName.key}`] = 0;
              totals[`renew_success_${packageName.key}`] = 0;
              totals[`renew_fail_${packageName.key}`] = 0;
              totals[`user_renew_total_${packageName.key}`] = 0;
              totals[`user_renew_success_${packageName.key}`] = 0;
              totals[`user_renew_fail_${packageName.key}`] = 0;
              totals[`cancel_total_${packageName.key}`] = 0;
              totals[`cancel_user_${packageName.key}`] = 0;
              totals[`cancel_system_${packageName.key}`] = 0;
              totals[`user_cancel_web_${packageName.key}`] = 0;
              totals[`user_cancel_ussd_${packageName.key}`] = 0;
              totals[`user_cancel_sms_${packageName.key}`] = 0;
              totals[`user_cancel_system_${packageName.key}`] = 0;
            });

            pageData.forEach((row) => {
              Object.keys(totals).forEach((key) => {
                totals[key] += row[key] || 0;
              });
            });

            const summaryValues = ['Total'];

            ['register', 'renew', 'user_renew', 'cancel', 'user_cancel'].forEach((metricGroup) => {
              if (metricGroup === 'register') {
                ['total', 'success', 'fail'].forEach((subMetric) => {
                  uniquePackages.forEach((packageName) => {
                    summaryValues.push(
                      totals[`${metricGroup}_${subMetric}_${packageName.key}`] || 0
                    );
                  });
                });
              } else if (metricGroup === 'renew') {
                ['total', 'success', 'fail'].forEach((subMetric) => {
                  uniquePackages.forEach((packageName) => {
                    summaryValues.push(
                      totals[`${metricGroup}_${subMetric}_${packageName.key}`] || 0
                    );
                  });
                });
              } else if (metricGroup === 'user_renew') {
                ['total', 'success', 'fail'].forEach((subMetric) => {
                  uniquePackages.forEach((packageName) => {
                    summaryValues.push(
                      totals[`${metricGroup}_${subMetric}_${packageName.key}`] || 0
                    );
                  });
                });
              } else if (metricGroup === 'cancel') {
                ['total', 'user', 'system'].forEach((subMetric) => {
                  uniquePackages.forEach((packageName) => {
                    summaryValues.push(
                      totals[`${metricGroup}_${subMetric}_${packageName.key}`] || 0
                    );
                  });
                });
              } else if (metricGroup === 'user_cancel') {
                ['web', 'ussd', 'sms', 'system'].forEach((subMetric) => {
                  uniquePackages.forEach((packageName) => {
                    summaryValues.push(
                      totals[`${metricGroup}_${subMetric}_${packageName.key}`] || 0
                    );
                  });
                });
              }
            });

            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  {summaryValues.map((value, index) => (
                    <Table.Summary.Cell
                      key={index}
                      index={index}
                      align="center"
                      className="font-medium"
                    >
                      {typeof value === 'number' ? formatNumber(value) : value}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            );
          }
        }}
      />
    </div>
  );
}

export default DetailTable;
